import Plivo from "plivo-browser-sdk";
import api from "@/lib/api";
import { normalizePhone } from "@/lib/phone";

/**
 * Plivo browser-calling engine — mirrors the twilioDevice interface so the shared
 * softphone UI works unchanged when the active provider is "plivo".
 *
 * Model (browser-initiated WebRTC, like Twilio): the agent's browser logs in as a
 * Plivo Endpoint (auto-provisioned by the backend) and places/receives calls
 * directly. Outbound: `connect()` calls `client.call(to_number)`; Plivo invokes our
 * Application answer URL which returns Dial XML to the PSTN. Inbound: Plivo rings
 * this endpoint → `onIncomingCall`.
 */
class PlivoDeviceManager {
    constructor() {
        this.sdk = null;
        this.client = null;
        this.registered = false;
        this.identity = null;
        this.incomingUUID = null;
        this.autoAccept = false;
        this.listeners = new Set();
        this._ensuring = null;
    }

    on(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
    emit(event, payload = {}) { for (const fn of this.listeners) fn({ event, ...payload }); }

    async ensureDevice() {
        if (this.registered && this.client) return this.client;
        if (this._ensuring) return this._ensuring;
        this._ensuring = this._register();
        try { return await this._ensuring; }
        finally { this._ensuring = null; }
    }

    async _register() {
        const { data: cfg } = await api.get("/plivo/session");
        console.log("[plivoDevice] session cfg:", { ...cfg, password: "***" });
        this.identity = cfg.username;
        this.callerId = cfg.caller_id || "";

        const sdk = new Plivo({
            debug: "WARN",
            permOnClick: true,
            enableTracking: true,
            closeProtection: true,
            allowMultipleIncomingCalls: false,
        });
        const client = sdk.client || sdk;
        this.sdk = sdk;
        this.client = client;

        client.on("onLogin", () => { this.registered = true; this.emit("registered"); });
        client.on("onLoginFailed", (cause) => {
            this.registered = false;
            this.emit("error", { error: `Plivo login failed: ${cause}` });
        });
        client.on("onLogout", () => { this.registered = false; this.emit("unregistered"); });
        client.on("onIncomingCall", (callerId, _extra, info) => {
            this.incomingUUID = info?.callUUID || this.client?.getIncomingCalls?.()?.[0] || null;
            this.emit("incoming", { from: normalizePhone(callerId), callSid: this.incomingUUID, autoAccepted: this.autoAccept });
            if (this.autoAccept && this.incomingUUID) {
                try { client.answer(this.incomingUUID); } catch (_e) {}
            }
        });
        client.on("onIncomingCallCanceled", () => { this.incomingUUID = null; this.emit("call:cancel"); });
        client.on("onCallAnswered", () => this.emit("call:accept", { sid: this.getActiveCallSid(), inbound: !!this.incomingUUID }));
        client.on("onMediaConnected", () => {});
        client.on("onCallTerminated", () => {
            this.incomingUUID = null;
            if (this.monitoring) { this.monitoring = false; this.emit("monitor:end"); }
            else this.emit("call:disconnect");
        });
        client.on("onCallFailed", (cause) => {
            this.incomingUUID = null;
            const c = String(cause || "").toLowerCase();
            // Agent hung up / cancelled a still-ringing leg (or a normal clearing) — this is
            // not a failure, so finish the call cleanly instead of surfacing a red error.
            const benign = c.includes("cancel") || c.includes("normal") || c.includes("hangup")
                || c.includes("bye") || c.includes("gone") || c.includes("originator");
            if (this.monitoring) { this.monitoring = false; this.emit("monitor:error", { error: `Plivo monitor failed: ${cause}` }); }
            else if (benign) { this.emit("call:disconnect"); }
            else { this.emit("call:error", { error: `Plivo call failed: ${cause}` }); }
        });
        client.on("onPermissionDenied", () => this.emit("error", { error: "Microphone permission is required for browser calls." }));

        return await new Promise((resolve, reject) => {
            try {
                client.login(cfg.username, cfg.password);
                const t = setTimeout(() => {
                    if (this.registered) resolve(client);
                    else { this.emit("error", { error: "Plivo registration timed out" }); reject(new Error("Plivo registration timed out")); }
                }, 8000);
                const off = this.on((e) => {
                    if (e.event === "registered") { clearTimeout(t); off(); resolve(client); }
                });
            } catch (e) {
                console.error("[plivoDevice] login threw", e);
                reject(e);
            }
        });
    }

    /** Browser-originate the outbound call to the PSTN number. */
    async connect({ to_number, internal_call_id }) {
        await this.primeAudio();
        await this.ensureDevice();
        const headers = internal_call_id ? { "X-PH-Callid": String(internal_call_id) } : {};
        console.log("[plivoDevice] call:", { to_number, internal_call_id });
        this.client.call(to_number, headers);
        this.emit("call:ringing");
        return true;
    }

    disconnect() { try { this.client?.hangup(); } catch (_e) {} }
    mute(muted) { try { muted ? this.client?.mute() : this.client?.unmute(); } catch (_e) {} }
    acceptIncoming() { try { if (this.incomingUUID) this.client?.answer(this.incomingUUID); } catch (_e) {} }
    rejectIncoming() { try { if (this.incomingUUID) this.client?.reject(this.incomingUUID); this.incomingUUID = null; } catch (_e) {} }
    setAutoAccept(v) { this.autoAccept = !!v; }
    getActiveCallSid() { try { return this.client?.getCallUUID?.() || null; } catch (_e) { return null; } }
    requestDial(number, opts = {}) { this.emit("dialer:open", { number, autoDial: !!opts.autoDial, campaign: !!opts.campaign }); }

    async primeAudio() {
        try {
            if (navigator?.mediaDevices?.getUserMedia) {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                stream.getTracks().forEach((t) => t.stop());
            }
        } catch (e) {
            this.emit("error", { error: "Microphone permission is required to make browser calls." });
            throw new Error("Microphone permission denied/unavailable");
        }
    }

    async reinit() { this.registered = false; this.client = null; this.sdk = null; return this.ensureDevice(); }
    isReady() { return !!this.client && this.registered; }

    /** Supervisor joins a live call's conference muted (listen-only). We place a
     *  call tagged with X-PH-Monitor; the Answer URL returns <Conference muted>. */
    async connectMonitor(room) {
        await this.ensureDevice();
        const dest = this.callerId || room;
        this.client.call(dest, { "X-PH-Monitor": "1", "X-PH-Room": String(room) });
        this.monitoring = true;
        return true;
    }

    stopMonitor() {
        try { this.client?.hangup(); } catch (_e) {}
        this.monitoring = false;
    }

    debugState() { return { identity: this.identity, registered: this.registered, incomingUUID: this.incomingUUID }; }
}

const plivoDevice = new PlivoDeviceManager();
if (typeof window !== "undefined") window.plivoDevice = plivoDevice;
export default plivoDevice;
